import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/Sidebar.css";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [galleryExpanded, setGalleryExpanded] = useState(false);
  const [booksExpanded, setBooksExpanded] = useState(false);
  const [hymnPlaying, setHymnPlaying] = useState(false);
  const hymnAudioRef = useRef(null);

  const selectedItem = useMemo(() => {
    const path = location.pathname;
    if (path === "/") return "home";
    if (path.startsWith("/guestbook")) return "guestbook";
    if (path === "/obituary" || path === "/about") return "obituary";
    if (path === "/memorial-booklet") return "memorial-booklet";
    if (path === "/bury-me-in-aychieh") return "bury-me-in-aychieh";
    if (path === "/live") return "live";
    if (path === "/abouna-life-and-legacy") return "abouna-life-and-legacy";
    if (path === "/bishop-tabet") return "bishop-tabet";
    if (path === "/family-eulogy") return "family-eulogy";
    if (path.startsWith("/books")) return "books";
    if (path.startsWith("/gallery")) return "gallery";
    if (path === "/facebook") return "facebook";
    if (path === "/contact") return "contact";
    return "home";
  }, [location.pathname]);

  const galleryView = useMemo(
    () => (location.pathname.startsWith("/gallery/videos") ? "videos" : "photos"),
    [location.pathname],
  );

  useEffect(() => {
    if (location.pathname.startsWith("/gallery")) setGalleryExpanded(true);
    if (location.pathname.startsWith("/books")) setBooksExpanded(true);
  }, [location.pathname]);

  const go = (path) => navigate(path);

  const toggleBooks = () => {
    const next = !booksExpanded;
    setBooksExpanded(next);
    if (next && selectedItem !== "books") go("/books");
  };

  const toggleGallery = () => {
    const next = !galleryExpanded;
    setGalleryExpanded(next);
    if (next && selectedItem !== "gallery") go("/gallery/photos");
  };

  const toggleHymn = async () => {
    const audio = hymnAudioRef.current;
    if (!audio) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch {
        setHymnPlaying(false);
      }
    } else {
      audio.pause();
    }
  };

  const menuButton = (key, label, path) => (
    <li className="sidebar-item" key={key}>
      <button
        type="button"
        className={`sidebar-link ${selectedItem === key ? "active" : ""}`}
        onClick={() => go(path)}
      >
        <span className="sidebar-text">{label}</span>
      </button>
    </li>
  );

  return (
    <div className="sidebar-inner" role="navigation" aria-label="Primary">
      <div className="sidebar-header">
        <div className="sidebar-header__cross" aria-hidden="true">✝</div>
        <p className="sidebar-header__eyebrow">Maronite Catholic Priest</p>
        <h2>Reverend Father<br />Paul Oneid</h2>
        <div className="sidebar-header__ornament" aria-hidden="true"><span>◆</span></div>
      </div>

      <nav className="sidebar-nav">
        <ul className="sidebar-menu">
          {menuButton("home", "Home", "/")}
          {menuButton("guestbook", "In-Memory Guestbook", "/guestbook")}
          {menuButton("obituary", "Obituary", "/obituary")}
          {menuButton("memorial-booklet", "Memorial Booklet", "/memorial-booklet")}
          {menuButton("bury-me-in-aychieh", "Bury Me in Aychieh", "/bury-me-in-aychieh")}
          {menuButton("live", "Live Stream & Recordings", "/live")}
          {menuButton("abouna-life-and-legacy", "Abouna: Life & Legacy", "/abouna-life-and-legacy")}
          {menuButton("bishop-tabet", "Excellency Bishop Tabet", "/bishop-tabet")}
          {menuButton("family-eulogy", "Eulogy by Family", "/family-eulogy")}

          <li className="sidebar-item">
            <button
              type="button"
              className={`sidebar-link expandable ${booksExpanded ? "expanded" : ""} ${selectedItem === "books" ? "active" : ""}`}
              aria-expanded={booksExpanded}
              aria-controls="menu-books"
              onClick={toggleBooks}
            >
              <span className="sidebar-text">Books</span>
              <span className="expand-arrow" aria-hidden="true" />
            </button>
            {booksExpanded && (
              <ul className="sidebar-submenu books-submenu" id="menu-books">
                <li className="sidebar-subitem">
                  <button
                    type="button"
                    className={`sidebar-sublink ${selectedItem === "books" ? "active" : ""}`}
                    onClick={() => go("/books")}
                  >
                    All Books
                  </button>
                </li>
              </ul>
            )}
          </li>

          <li className="sidebar-item">
            <button
              type="button"
              className={`sidebar-link expandable ${galleryExpanded ? "expanded" : ""} ${selectedItem === "gallery" ? "active" : ""}`}
              aria-expanded={galleryExpanded}
              aria-controls="menu-gallery"
              onClick={toggleGallery}
            >
              <span className="sidebar-text">Media Gallery</span>
              <span className="expand-arrow" aria-hidden="true" />
            </button>
            {galleryExpanded && (
              <ul className="sidebar-submenu" id="menu-gallery">
                <li className="sidebar-subitem">
                  <button
                    type="button"
                    className={`sidebar-sublink ${galleryView === "photos" ? "active" : ""}`}
                    onClick={() => go("/gallery/photos")}
                  >
                    Photos
                  </button>
                </li>
                <li className="sidebar-subitem">
                  <button
                    type="button"
                    className={`sidebar-sublink ${galleryView === "videos" ? "active" : ""}`}
                    onClick={() => go("/gallery/videos")}
                  >
                    Videos
                  </button>
                </li>
              </ul>
            )}
          </li>

          {menuButton("facebook", "Facebook Posts", "/facebook")}
          {menuButton("contact", "Contact", "/contact")}

          <li className="sidebar-item sidebar-hymn-item">
            <button
              type="button"
              className={`sidebar-link sidebar-hymn-button ${hymnPlaying ? "playing" : ""}`}
              onClick={toggleHymn}
              aria-pressed={hymnPlaying}
              aria-label={`${hymnPlaying ? "Pause" : "Play"} Mother of God hymn`}
            >
              <span className="sidebar-hymn-icon" aria-hidden="true" />
              <span className="sidebar-hymn-label">
                <span>Mother of God Hymn</span>
                <span lang="ar" dir="rtl">يا أم الله</span>
              </span>
            </button>
            <audio
              ref={hymnAudioRef}
              src="/media/audio/mother-of-god-hymn.m4a"
              preload="metadata"
              onPlay={() => setHymnPlaying(true)}
              onPause={() => setHymnPlaying(false)}
              onEnded={() => setHymnPlaying(false)}
            />
          </li>
        </ul>
      </nav>
    </div>
  );
}
